import React, { useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, useController } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import { TooltipContent } from "components/form_components/dropdown_list_selects/new_supplier_selects/SupplierTooltip";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput } from "components/ui/combobox";
import { InfiniteComboboxList } from "components/ui/combobox/InfiniteComboboxList";
import type { ComboboxData, Option, OptionValue } from "components/ui/combobox/types";
import { useDebouncedValue } from "lib/debounce";
import {
  useInfiniteOrganisations,
  useOrganisations,
} from "lib/hooks/api/organisations/useOrganisations";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { routes } from "lib/routes";
import { OrgSortBy, OrgSortOrder, SearchOrgRole } from "lib/types/graphQLEnums";
import { ComboboxProps } from "../../combobox/ComboBox";
import { EntityOption } from "./ComboboxOption";
import { EmptyComboboxTrigger } from "./EmptyComboboxTrigger";
import { SelectedEntityCard } from "./SelectedEntityCard";

export type Supplier = {
  id: string;
  name: string;
  aliases?: string[];
  awardCount?: number;
};

function ComboboxContentComponent(props: {
  combobox: ComboboxData;
  placeholder: string;
  setIsOpen: (open: boolean) => void;
  setSearchText: (text: string) => void;
  disabled?: boolean;
  loading: boolean;
  getSupplier: (id: string) => Supplier | null;
  trigger?: React.ReactNode;
  fetchNextPage: () => void;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  showSupplierTooltips?: boolean;
}) {
  const {
    combobox,
    placeholder,
    setIsOpen,
    setSearchText,
    disabled = false,
    getSupplier,
    trigger,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    loading,
    showSupplierTooltips = false,
  } = props;

  const toolTipProps = showSupplierTooltips
    ? {
        itemTooltip: (option: Option) => {
          const supplier = getSupplier(String(option.value));
          if (!supplier) return null;
          return (
            <TooltipContent
              supplierName={supplier.name}
              aliases={supplier.aliases}
              totalNotices={supplier.awardCount}
            />
          );
        },
      }
    : {};

  return (
    <ComboboxContent
      open={combobox.open}
      setOpen={(open) => {
        combobox.setOpen(open);
        setIsOpen(open);
      }}
      trigger={trigger ?? <div />}
    >
      <ComboboxInput
        placeholder={placeholder}
        filterValue={combobox.filterValue}
        setFilterValue={(value) => {
          combobox.setFilterValue(value);
          setSearchText(value);
        }}
        open={combobox.open}
        setOpen={combobox.setOpen}
        value={combobox.value}
        items={combobox.items}
        multiple={combobox.multiple}
        inputRef={combobox.inputRef}
        inputElementRef={combobox.inputElementRef}
        disabled={disabled}
      />
      <ComboboxEmpty
        filteredItems={combobox.filteredItems}
        filterValue={combobox.filterValue}
        disableAddingOptions
      >
        {loading ? (
          <LoadingContainer>
            <UIcon icon="spinner" />
          </LoadingContainer>
        ) : (
          "No suppliers found"
        )}
      </ComboboxEmpty>

      <InfiniteComboboxList
        items={combobox.items as Option[]}
        filteredItems={combobox.filteredItems as Option[]}
        value={combobox.value}
        selectItem={combobox.selectItem}
        multiple={combobox.multiple}
        highlightedIndex={combobox.highlightedIndex}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage || false}
        isFetchingNextPage={isFetchingNextPage}
        scrollAreaId="supplier-scroll-area"
        {...toolTipProps}
      >
        {(option: Option) => {
          const supplier = getSupplier(String(option.value));
          return (
            <EntityOption label={supplier ? supplier.name : option.label} dataType="supplier" />
          );
        }}
      </InfiniteComboboxList>
    </ComboboxContent>
  );
}

type SuppliersComboboxProps<TFieldValues extends FieldValues = FieldValues> = Required<
  Pick<ComboboxProps<TFieldValues>, "control" | "name">
> & {
  placeholder?: string;
  disabled?: boolean;
  trigger?: React.ReactNode;
};

export function SuppliersCombobox<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  placeholder = "Search suppliers...",
  disabled = false,
  trigger,
}: SuppliersComboboxProps<TFieldValues>) {
  const { field } = useController({ control, name });
  const [searchText, setSearchText] = useState("");
  const [debouncedText] = useDebouncedValue(searchText, 300);
  const [isOpen, setIsOpen] = useState(false);
  const [clickedSupplierId, setClickedSupplierId] = useState<string | null>(null);
  const prevSuppliersCountRef = useRef(0);

  // Fetch selected suppliers by ID (when we have IDs but no search)
  const fetchSelectedSuppliers =
    !!field.value && !debouncedText && field.value.length > 0 && !isOpen;

  const { data: selectedSuppliers, isFetching: isFetchingSelected } = useOrganisations(
    {
      limit: field.value?.length || 20,
      page: 1,
      ids: field.value,
      sortBy: OrgSortBy.AwardCount,
      sortOrder: OrgSortOrder.Desc,
      role: SearchOrgRole.Supplier,
      include: { awardCount: true, noticeCount: false, aliases: true },
    },
    {
      enabled: fetchSelectedSuppliers,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  // Persistent cache of all known suppliers from both queries.
  const [knownSuppliers, setKnownSuppliers] = useState<Map<string, Supplier>>(new Map());

  // Search for suppliers based on query or show initial set when opening dropdown
  const {
    data: dropdownSuppliersData,
    fetchNextPage,
    hasNextPage,
    isFetching: isLoadingDropdownSuppliers,
    isFetchingNextPage,
  } = useInfiniteOrganisations({
    limit: 100,
    page: 1,
    names: debouncedText.length ? [debouncedText] : [],
    sortBy: OrgSortBy.AwardCount,
    sortOrder: OrgSortOrder.Desc,
    role: SearchOrgRole.Supplier,
    include: { awardCount: true, noticeCount: false, aliases: true },
  });

  // Flatten pages data
  const dropdownSuppliers = useMemo(
    () => ({
      searchOrganisations: {
        orgs: dropdownSuppliersData?.pages?.flatMap((page) => page.searchOrganisations.orgs) || [],
      },
    }),
    [dropdownSuppliersData],
  );

  // Accumulate supplier data from both queries into the persistent cache
  useEffect(() => {
    const allOrgs = [
      ...(selectedSuppliers?.searchOrganisations.orgs ?? []),
      ...(dropdownSuppliers?.searchOrganisations.orgs ?? []),
    ];
    if (allOrgs.length === 0) return;

    setKnownSuppliers((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const org of allOrgs) {
        if (!next.has(org.id)) {
          next.set(org.id, {
            id: org.id,
            name: org.name,
            aliases: org.aliases ?? [],
            awardCount: org.awardCount ?? 0,
          });
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selectedSuppliers, dropdownSuppliers]);

  // Display suppliers = suppliers in field.value that we have data for
  const displaySuppliers: Supplier[] = useMemo(() => {
    const fieldIds: string[] = field.value ?? [];
    if (fieldIds.length === 0) return [];
    return fieldIds
      .map((id) => knownSuppliers.get(id))
      .filter((s): s is Supplier => s !== undefined);
  }, [field.value, knownSuppliers]);

  // Keep combobox open when a supplier is added from empty state
  useEffect(() => {
    const prevCount = prevSuppliersCountRef.current;
    const currentCount = displaySuppliers.length;

    // If we went from 0 to 1+ suppliers and combobox is open, set clicked ID to the first supplier
    if (prevCount === 0 && currentCount > 0 && isOpen) {
      setClickedSupplierId(displaySuppliers[0].id);
    }

    prevSuppliersCountRef.current = currentCount;
  }, [displaySuppliers, isOpen]);

  const options: Option[] = useMemo(() => {
    const searchSuppliers = dropdownSuppliers?.searchOrganisations.orgs || [];
    const selectedOrgs = selectedSuppliers?.searchOrganisations.orgs || [];

    // Create a map to avoid duplicates
    const supplierMap = new Map<string, Option>();

    // Add all selected suppliers first (so they appear even during search)
    selectedOrgs.forEach((supplier) => {
      if (!displaySuppliers.some((s) => s.id === supplier.id)) {
        supplierMap.set(supplier.id, { value: supplier.id, label: supplier.name });
      }
    });

    // Add search results, but don't overwrite selected suppliers
    searchSuppliers.forEach((supplier) => {
      if (!supplierMap.has(supplier.id) && !displaySuppliers.some((s) => s.id === supplier.id)) {
        supplierMap.set(supplier.id, { value: supplier.id, label: supplier.name });
      }
    });

    return [
      ...displaySuppliers.map((s) => ({ value: s.id, label: s.name })),
      ...supplierMap.values(),
    ];
  }, [dropdownSuppliers, selectedSuppliers, displaySuppliers]);

  const getSupplier = useMemo(
    () =>
      (id: string): Supplier | null => {
        const allSuppliers = [
          ...(selectedSuppliers?.searchOrganisations.orgs || []),
          ...(dropdownSuppliers?.searchOrganisations.orgs || []),
        ];
        const supplier = allSuppliers.find((s) => s.id === id);
        if (!supplier) return null;
        return {
          id: supplier.id,
          name: supplier.name,
          aliases: supplier.aliases ?? [],
          awardCount: supplier.awardCount ?? 0,
        };
      },
    [selectedSuppliers, dropdownSuppliers],
  );

  const loading = isFetchingSelected || isLoadingDropdownSuppliers;

  // Convert field value (array of strings) to array of OptionValue
  const comboboxValue: OptionValue[] = field.value || [];

  const handleChange = (newValues: OptionValue[]) => {
    field.onChange(newValues);
  };

  const openCombobox = (combobox: ComboboxData) => {
    combobox.setOpen(true);
    setIsOpen(true);
  };

  return (
    <Combobox
      items={options}
      value={comboboxValue}
      onChange={handleChange}
      multiple
      externalFilter={!loading}
    >
      {(combobox) => (
        <PropertyInnerContainer>
          <Flex column gap={8} width="100%" minWidth={0}>
            {displaySuppliers.length > 0 ? (
              displaySuppliers.map((supplier) => (
                <React.Fragment key={supplier.id}>
                  <SelectedEntityCard
                    entity={supplier}
                    dataType="supplier"
                    combobox={combobox}
                    setIsOpen={setIsOpen}
                    setEntityClickerId={setClickedSupplierId}
                    clickedEntityId={clickedSupplierId}
                    linkButtonProps={{ href: `${routes.supplier}/${supplier.id}` }}
                  />
                  {clickedSupplierId === supplier.id && (
                    <ComboboxContentComponent
                      combobox={combobox}
                      placeholder={placeholder}
                      setIsOpen={setIsOpen}
                      setSearchText={setSearchText}
                      disabled={disabled}
                      loading={loading}
                      getSupplier={getSupplier}
                      fetchNextPage={fetchNextPage}
                      hasNextPage={hasNextPage}
                      isFetchingNextPage={isFetchingNextPage}
                      showSupplierTooltips={false}
                    />
                  )}
                </React.Fragment>
              ))
            ) : (
              <ComboboxContentComponent
                combobox={combobox}
                placeholder={placeholder}
                setIsOpen={setIsOpen}
                setSearchText={setSearchText}
                disabled={disabled}
                loading={loading}
                getSupplier={getSupplier}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                showSupplierTooltips={false}
                trigger={
                  trigger ? (
                    // Wrap trigger in a span to handle onClick and onKeyDown for accessibility
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => openCombobox(combobox)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openCombobox(combobox);
                        }
                      }}
                    >
                      {trigger}
                    </span>
                  ) : (
                    <EmptyComboboxTrigger
                      label="Supplier"
                      disabled={isFetchingSelected}
                      onClick={() => {
                        openCombobox(combobox);
                      }}
                    />
                  )
                }
              />
            )}
          </Flex>
        </PropertyInnerContainer>
      )}
    </Combobox>
  );
}

const PropertyInnerContainer = styled.div({
  minWidth: 0,
  width: "100%",
});

const LoadingContainer = styled.div({
  display: "flex",
  justifyContent: "center",
  padding: "8px",
});
